import { useTar } from '@/modules'
import { cn } from '@duck/ui'
import { MessageSquarePlus } from 'lucide-react'

export type SettingsNavKey = 'challenge' | 'appearance' | 'behavior' | 'python' | 'shortcuts'

interface WorkspaceSettingsNavProps {
  active: SettingsNavKey
  onSelect: (key: SettingsNavKey) => void
  showChallenge: boolean
  onSendFeedback: () => void
}

export const WorkspaceSettingsNav = ({ active, onSelect, showChallenge, onSendFeedback }: WorkspaceSettingsNavProps) => {
  const { t } = useTar()

  const itemClass = (key: SettingsNavKey) =>
    cn(
      'w-full cursor-pointer rounded px-3 py-2 text-start text-sm transition-colors',
      active === key
        ? 'bg-accent/40 text-foreground border-s-2 border-primary'
        : 'text-muted-foreground hover:bg-accent/20'
    )

  return (
    <div className="flex h-full w-[200px] flex-col border-e border-border bg-background py-4 rounded-sm">
      <div className="px-4 pb-3 text-base font-semibold text-foreground">
        {t('common.settings')}
      </div>

      {showChallenge && (
        <div className="px-2">
          <button type="button" className={itemClass('challenge')} onClick={() => onSelect('challenge')}>
            {t('common.settingsThisChallenge')}
          </button>
        </div>
      )}

      <div className="mt-4 px-4 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {t('common.settingsPreferences')}
      </div>
      <div className="px-2">
        <button type="button" className={itemClass('appearance')} onClick={() => onSelect('appearance')}>
          {t('common.settingsAppearance')}
        </button>
        <button type="button" className={itemClass('behavior')} onClick={() => onSelect('behavior')}>
          {t('common.settingsBehavior')}
        </button>
        <button type="button" className={itemClass('python')} onClick={() => onSelect('python')}>
          {t('common.settingsPython')}
        </button>
        <button type="button" className={itemClass('shortcuts')} onClick={() => onSelect('shortcuts')}>
          {t('common.shortcutsTitle')}
        </button>
      </div>

      <div className="flex-1" />
      <div className="px-2">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center gap-2 rounded px-3 py-2 text-start text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:text-foreground"
          onClick={onSendFeedback}
          data-testid="workspace-settings-feedback"
        >
          <MessageSquarePlus className="size-4 shrink-0" />
          {t('userVoice.menuItem')}
        </button>
      </div>
      <div className="mt-3 border-t border-border px-4 pt-3 text-[11px] text-muted-foreground">
        {t('common.settingsChangesApplyInstantly')}
      </div>
    </div>
  )
}
